import { FormField } from "@common/types";
import { useFormikContext } from "formik";
import { useCallback, useEffect, useState } from "react";
import { set } from "lodash";

interface Props {
  when: FormField | FormField[] | any;
  is: (...values: unknown[]) => boolean;
  onHide?: (reset: (what: FormField | FormField[] | any, value?: unknown) => void) => void;
  children?: React.ReactNode;
}

export const Conditional: React.FC<Props> = ({ when, is, onHide, children }) => {
  const formFields = Array.isArray(when) ? when : [when];
  const { setFieldValue, setFieldTouched, getFieldProps, setValues, values: formValues } = useFormikContext();
  const values = formFields.map((formField) => getFieldProps(formField.fieldName).value);
  const currentlyVisible = is(...values);
  const [previouslyVisible, setPreviouslyVisible] = useState(currentlyVisible);

  const onHideCallback = useCallback(() => {
    onHide?.((what, value = "") => {
      const resetFormFields = Array.isArray(what) ? what : [what];
      let resetValues = Array.isArray(value) ? value : [value];
      if (!value) {
        resetValues = [];
        resetFormFields.forEach(() => resetValues.push(""));
      }
      const data = formValues as any;
      resetFormFields.forEach((formField, index) => {
        const currentValue = getFieldProps(formField.fieldName).value;
        const newValue = resetValues?.[index];
        if (newValue !== currentValue) {
          set(data, formField.fieldName, newValue);
          if (newValue === undefined) {
            setValues(data);
          } else {
            setFieldValue(formField.fieldName, newValue);
          }
          setFieldTouched(formField.fieldName, false);
        }
      });
    });
  }, [onHide, formValues, setFieldValue, setFieldTouched, getFieldProps, setValues]);

  useEffect(() => {
    if (currentlyVisible !== previouslyVisible) {
      if (previouslyVisible && !currentlyVisible) {
        onHideCallback();
      }
      setPreviouslyVisible(currentlyVisible);
    }
  }, [previouslyVisible, currentlyVisible, onHideCallback]);

  if (currentlyVisible) {
    return <>{children}</>;
  }

  return null;
};
